import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './css/index.scss';
import MyModal from '../MyModal';
import MyButton from '../MyButton';
import MyCheckBox from '../MyCheckbox';
import Api from '../../Api';
import {
  getCategoriesRequest,
  getCountriesRequest,
  getLanguagesRequest,
} from '../../store/actions/staticData';
import {
  ALL_CITIES_OPTION_ID,
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from '../../helpers/allCitiesOfCountry';
import { fetchCitiesOfCountryCached } from '../../helpers/organizationsBulkAutofill';
import { saveBulkEditedItems } from '../../helpers/bulkEditSelected';

function FieldSelect({
  value, onChange, options, placeholder = '— Не выбрано —', disabled,
}) {
  return (
    <select
      className="bulk_edit_modal_select"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function useCitiesOfCountry(countryId) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!countryId) {
      setCities([]);
      return undefined;
    }
    setLoading(true);
    fetchCitiesOfCountryCached(countryId)
      .then((list) => { if (!cancelled) setCities(list); })
      .catch(() => { if (!cancelled) setCities([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryId]);

  return { cities, loading };
}

function CityField({ value, onChange, countries }) {
  const [countryId, setCountryId] = useState(value?.country_id || '');
  const { cities, loading } = useCitiesOfCountry(countryId);

  useEffect(() => {
    setCountryId(value?.country_id || '');
  }, [value?.country_id]);

  const country = useMemo(
    () => (countries || []).find((item) => item._id === countryId) || null,
    [countries, countryId],
  );

  const cityOptions = useMemo(() => ([
    { value: ALL_CITIES_OPTION_ID, label: 'Все города страны' },
    ...cities.map((city) => ({ value: city._id, label: city.name })),
  ]), [cities]);

  const selectedCityValue = isAllCitiesSelection(value) ? ALL_CITIES_OPTION_ID : (value?._id || '');

  return (
    <div className="bulk_edit_modal_field_body">
      <FieldSelect
        value={countryId}
        options={(countries || []).map((item) => ({ value: item._id, label: item.name }))}
        onChange={(id) => {
          setCountryId(id);
          onChange(null);
        }}
        placeholder="— Выберите страну —"
      />
      <FieldSelect
        value={selectedCityValue}
        options={cityOptions}
        onChange={(cityId) => {
          if (!cityId) {
            onChange(null);
            return;
          }
          if (cityId === ALL_CITIES_OPTION_ID) {
            onChange(buildAllCitiesSelection(country));
            return;
          }
          const city = cities.find((item) => item._id === cityId);
          onChange(city ? { ...city, is_in_all_cities_of_country: false } : null);
        }}
        placeholder={loading ? 'Загрузка городов…' : '— Выберите город —'}
        disabled={!countryId || loading}
      />
    </div>
  );
}

function CheckboxList({
  items, selectedIds, onToggle, emptyText, withFlags,
}) {
  if (!items?.length) {
    return <p className="bulk_edit_modal_note">{emptyText}</p>;
  }

  return (
    <div className="bulk_edit_modal_checkbox_list">
      {items.map((item) => (
        <MyCheckBox
          key={item._id}
          value={selectedIds.includes(item._id)}
          onClick={() => onToggle(item)}
          text={item.name}
          imgUrl={withFlags && item.flag_url ? Api.url + item.flag_url : undefined}
        />
      ))}
    </div>
  );
}

/**
 * entityType: 'organization' | 'event'
 * entityLabel: 'предложений' | 'мероприятий'
 */
function BulkEditSelectedModal({
  isOpen,
  onClose,
  entityType,
  entityLabel,
  selectedItems = [],
  onFinished,
}) {
  const dispatch = useDispatch();
  const { categories, countries, languages } = useSelector((store) => store.staticData);

  const [eventCategories, setEventCategories] = useState([]);
  const [enableCity, setEnableCity] = useState(false);
  const [enableCategory, setEnableCategory] = useState(false);
  const [enableSubCategories, setEnableSubCategories] = useState(false);
  const [enableLanguages, setEnableLanguages] = useState(false);

  const [city, setCity] = useState(null);
  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [isForAllLanguages, setIsForAllLanguages] = useState(true);

  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState('');

  const count = selectedItems.length;
  const isOrganization = entityType === 'organization';

  const categoryOptions = useMemo(() => {
    if (isOrganization) {
      return (categories || []).map((item) => ({
        value: item._id,
        label: `${item.name}${item.is_online ? ' — онлайн' : ''}`,
      }));
    }
    return (eventCategories || []).map((item) => ({
      value: item._id,
      label: item.name,
    }));
  }, [isOrganization, categories, eventCategories]);

  const categoryCatalog = isOrganization ? (categories || []) : (eventCategories || []);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getCountriesRequest());
    dispatch(getLanguagesRequest());
    if (isOrganization) {
      dispatch(getCategoriesRequest());
    } else {
      Api.getEventCategories()
        .then(({ data }) => {
          setEventCategories((data?.categories || []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)));
        })
        .catch(() => setEventCategories([]));
    }
  }, [isOpen, isOrganization, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    setEnableCity(false);
    setEnableCategory(false);
    setEnableSubCategories(false);
    setEnableLanguages(false);
    setCity(null);
    setCategory(null);
    setSubCategories([]);
    setSelectedLanguages([]);
    setIsForAllLanguages(true);
    setSaving(false);
    setProgress(null);
    setResult(null);
    setFormError('');
  }, [isOpen]);

  useEffect(() => {
    if (!enableCategory) {
      setEnableSubCategories(false);
      setSubCategories([]);
    }
  }, [enableCategory]);

  const close = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    setFormError('');
    if (!enableCity && !enableCategory && !enableSubCategories && !enableLanguages) {
      setFormError('Включите хотя бы одно поле для изменения.');
      return;
    }
    if (enableCity && !city) {
      setFormError('Выберите страну и город.');
      return;
    }
    if (enableCategory && !category) {
      setFormError('Выберите категорию.');
      return;
    }
    if (enableSubCategories && !category?.sub_categories?.length) {
      setFormError('У выбранной категории нет подкатегорий.');
      return;
    }
    if (enableLanguages && !isForAllLanguages && !selectedLanguages.length) {
      setFormError('Выберите хотя бы один язык или отметьте «для всех языков».');
      return;
    }

    const overrides = {};
    if (enableCity) overrides.city = city;
    if (enableCategory) overrides.category = category;
    if (enableSubCategories) overrides.subCategories = subCategories;
    if (enableLanguages) {
      overrides.languages = selectedLanguages;
      overrides.isForAllLanguages = isForAllLanguages;
    }

    setSaving(true);
    setProgress({ current: 0, total: count });
    const saveResult = await saveBulkEditedItems({
      entityType,
      items: selectedItems,
      overrides,
      catalogs: {
        categories: isOrganization ? categories : undefined,
        languages,
      },
      onProgress: setProgress,
    });
    setSaving(false);
    setProgress(null);
    setResult(saveResult);
  };

  const handleFinish = () => {
    onFinished?.(result);
    onClose();
  };

  const resultErrorsText = (result?.failures || [])
    .map((item) => `${item.name}\nID: ${item.id}\n${item.message}`)
    .join('\n\n');

  return (
    <MyModal
      isOpen={isOpen}
      closeFunction={close}
      closeOnPressOutside={!saving}
      style={{
        width: 'min(560px, 92vw)',
        maxHeight: 'min(80vh, 720px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="bulk_edit_modal">
        <p className="bulk_edit_modal_title">Редактировать выбранные</p>
        <p className="bulk_edit_modal_subtitle">{`Редактируем ${count} ${entityLabel}`}</p>
        <p className="bulk_edit_modal_warning">
          Учтите: этот функционал при сохранении нагружает систему — пользуйтесь с умом.
          Запросы уходят по одной записи последовательно.
        </p>

        {result ? (
          <div className="bulk_edit_modal_result">
            <p className="bulk_edit_modal_result_summary">
              {`${result.successCount}/${result.total} ${entityLabel} успешно сохранены.`}
            </p>
            {result.failures?.length ? (
              <pre className="bulk_edit_modal_result_errors">{resultErrorsText}</pre>
            ) : null}
            <div className="bulk_edit_modal_actions">
              <MyButton
                text="Ок"
                onClick={handleFinish}
                className="bulk_edit_modal_button"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="bulk_edit_modal_body">
              <div className="bulk_edit_modal_field">
                <div className="bulk_edit_modal_field_head">
                  <p className="bulk_edit_modal_field_label">Страна и город</p>
                  <MyCheckBox
                    value={enableCity}
                    onClick={() => setEnableCity((prev) => !prev)}
                    text="Изменить"
                    wrapperClassName="bulk_edit_modal_field_enable"
                  />
                </div>
                {enableCity ? (
                  <CityField value={city} onChange={setCity} countries={countries || []} />
                ) : null}
              </div>

              <div className="bulk_edit_modal_field">
                <div className="bulk_edit_modal_field_head">
                  <p className="bulk_edit_modal_field_label">Категория</p>
                  <MyCheckBox
                    value={enableCategory}
                    onClick={() => setEnableCategory((prev) => !prev)}
                    text="Изменить"
                    wrapperClassName="bulk_edit_modal_field_enable"
                  />
                </div>
                {enableCategory ? (
                  <div className="bulk_edit_modal_field_body">
                    <FieldSelect
                      value={category?._id}
                      options={categoryOptions}
                      onChange={(id) => {
                        const next = categoryCatalog.find((item) => item._id === id) || null;
                        setCategory(next);
                        setSubCategories([]);
                      }}
                      placeholder="— Выберите категорию —"
                    />
                    {isOrganization && category?.sub_categories?.length ? (
                      <p className="bulk_edit_modal_note">
                        У категории есть подкатегории — лучше сразу задать их ниже, иначе у части записей сохранение может не пройти.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {isOrganization ? (
                <div className="bulk_edit_modal_field">
                  <div className="bulk_edit_modal_field_head">
                    <p className="bulk_edit_modal_field_label">Подкатегории</p>
                    <MyCheckBox
                      value={enableSubCategories}
                      onClick={() => {
                        if (!enableCategory || !category) return;
                        setEnableSubCategories((prev) => !prev);
                      }}
                      text="Изменить"
                      wrapperClassName="bulk_edit_modal_field_enable"
                      disabled={!enableCategory || !category}
                    />
                  </div>
                  {!enableCategory || !category ? (
                    <p className="bulk_edit_modal_note">Сначала включите и выберите категорию.</p>
                  ) : enableSubCategories ? (
                    <CheckboxList
                      items={category.sub_categories || []}
                      selectedIds={subCategories.map((item) => item._id)}
                      onToggle={(item) => {
                        const ids = subCategories.map((sub) => sub._id);
                        if (ids.includes(item._id)) {
                          setSubCategories(subCategories.filter((sub) => sub._id !== item._id));
                        } else {
                          setSubCategories([...subCategories, item]);
                        }
                      }}
                      emptyText="У этой категории нет подкатегорий."
                    />
                  ) : null}
                </div>
              ) : null}

              <div className="bulk_edit_modal_field">
                <div className="bulk_edit_modal_field_head">
                  <p className="bulk_edit_modal_field_label">Языки</p>
                  <MyCheckBox
                    value={enableLanguages}
                    onClick={() => setEnableLanguages((prev) => !prev)}
                    text="Изменить"
                    wrapperClassName="bulk_edit_modal_field_enable"
                  />
                </div>
                {enableLanguages ? (
                  <div className="bulk_edit_modal_field_body">
                    <MyCheckBox
                      value={isForAllLanguages}
                      onClick={() => setIsForAllLanguages((prev) => !prev)}
                      text="Карточка подходит для любых языков"
                    />
                    {!isForAllLanguages ? (
                      <CheckboxList
                        items={languages || []}
                        selectedIds={selectedLanguages.map((item) => item._id)}
                        onToggle={(item) => {
                          const ids = selectedLanguages.map((lang) => lang._id);
                          if (ids.includes(item._id)) {
                            setSelectedLanguages(selectedLanguages.filter((lang) => lang._id !== item._id));
                          } else {
                            setSelectedLanguages([...selectedLanguages, item]);
                          }
                        }}
                        emptyText="Справочник языков не загружен."
                        withFlags
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {formError ? <p className="bulk_edit_modal_note">{formError}</p> : null}
            {saving && progress ? (
              <p className="bulk_edit_modal_progress">
                {`Сохранение: ${progress.current} из ${progress.total}…`}
              </p>
            ) : null}

            <div className="bulk_edit_modal_actions">
              <MyButton
                text="Отмена"
                secondary
                onClick={close}
                disabled={saving}
                className="bulk_edit_modal_button"
              />
              <MyButton
                text="Сохранить"
                onClick={handleSave}
                loading={saving}
                disabled={saving || !count}
                className="bulk_edit_modal_button"
              />
            </div>
          </>
        )}
      </div>
    </MyModal>
  );
}

export default BulkEditSelectedModal;
